import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import SEO from "@/components/feature/SEO";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoCard from "@/components/base/VideoCard";
import { categories, videos } from "@/mocks/videos";

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const filteredVideos = videos.filter((v) => v.categorySlug === activeSlug);

  const categoryTitle = `${activeCategory.name} - 久久视频高清在线观看`;
  const categoryDesc = `${activeCategory.description}。久久视频聚合香蕉视频、红桃视频、91视频等平台${activeCategory.name}资源，高清不缓冲，追剧观影首选。`;

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: activeCategory.name },
  ];

  return (
    <>
      <SEO
        title={categoryTitle}
        description={categoryDesc}
        keywords={`${activeCategory.name},${activeCategory.name}在线观看,久久视频,香蕉视频,红桃视频,高清,免费`}
        ogType="website"
      />

      <main className="pt-20 pb-12">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          {/* Category Header */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
              {activeCategory.name}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">{activeCategory.description}</p>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-8 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeSlug === cat.slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                <i className={`${cat.icon} mr-1.5`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Stats */}
          <div className="flex items-center gap-4 mb-6 text-sm text-foreground-500">
            <span>共 <strong className="text-foreground-900">{filteredVideos.length}</strong> 部作品</span>
            <span className="text-foreground-300">|</span>
            <span>来源：香蕉视频、红桃视频、91视频等平台</span>
          </div>

          {/* Video Grid */}
          {filteredVideos.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop="">
              {filteredVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-video-off-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无视频，请查看其他分类</p>
            </div>
          )}

          {/* Bottom CTA */}
          <div className="mt-12 p-6 bg-accent-100/50 rounded-lg text-center">
            <p className="text-sm text-foreground-700 mb-3">
              没找到你想看的？试试搜索或浏览其他分类
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              {categories.filter(c => c.slug !== activeSlug).slice(0, 4).map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="px-4 py-1.5 rounded-full bg-background-50 text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap border border-background-200 cursor-pointer"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
    </>
  );
}